import { Editor, JSONContent } from '@tiptap/core';
import { ShadowSettingKey } from '../models/setting/shadow-setting';
import {
  editorUpdateShouldTargetChild,
  hasSelection,
  markUnderCursorForType,
  replaceSelectedText,
} from './editor-utils';
import { SetLinkCommandAttribute } from './extensions/link';
import { FontFamily, FontSize, FontWeight } from './font';

type TextEditorBooleanAttribute = {
  set: () => void;
  unset: () => void;
  toggle: () => void;
  get: () => boolean;
  setAll: () => void;
  unsetAll: () => void;
};

type TextEditorAttribute<AttributeType = string, GetReturn = AttributeType> = {
  set: (v: AttributeType) => void;
  unset: () => void;
  unsetAll: () => void;
  get: (mixedValue?: string, parentValue?: string) => GetReturn;
};

type TextEditorToggleAttribute = {
  toggle: () => void;
  get: () => boolean;
};

type TextEditorSelectionAttribute = {
  replace: (content: JSONContent) => void;
};

export type TextEditorHandler = {
  updateShouldTargetChild: (detailMode: boolean | undefined) => boolean;
  focus: {
    isFocused: () => boolean;
    keep: () => void;
    end: () => void;
  };
  blur: () => void;
  bold: TextEditorBooleanAttribute;
  fontWeight: {
    set: (v: FontWeight) => void;
  };
  fontFamily: TextEditorAttribute<FontFamily>;
  fontSize: TextEditorAttribute<FontSize>;
  color: TextEditorAttribute<string>;
  italic: TextEditorBooleanAttribute;
  bulletList: TextEditorToggleAttribute;
  orderedList: TextEditorToggleAttribute;
  shadow: TextEditorAttribute<ShadowSettingKey>;
  strikethrough: TextEditorToggleAttribute;
  underline: TextEditorToggleAttribute;
  subscript: TextEditorToggleAttribute;
  superscript: TextEditorToggleAttribute;
  link: TextEditorAttribute<SetLinkCommandAttribute, SetLinkCommandAttribute['href']>;
  selection: TextEditorSelectionAttribute;
};

// NOTE: There seems to a bug in chaining commands that are manipulating the same element/attribute: https://github.com/ueberdosis/tiptap/issues/5792
// Until this is resolved, make sure to call these commands separately rather than chaining them (see subscript toggle for an example)
export function createTextEditorHandler(editor: Editor | null): TextEditorHandler {
  return {
    updateShouldTargetChild: (detailMode) => {
      return editorUpdateShouldTargetChild(editor, detailMode);
    },
    focus: {
      isFocused: () => editor?.isFocused || false,
      keep: () => editor?.chain().focus().run(),
      end: () => editor?.commands.focus('end'),
    },
    blur: () => editor?.commands.blur(),
    bold: {
      set: () => editor?.chain().focus().setBold().run(),
      unset: () => editor?.chain().focus().unsetBold().run(),
      setAll: () => {
        editor?.chain().selectAll().setBold().blur().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetBold().blur().run();
      },
      toggle: () => {
        editor?.chain().focus().toggleBold().run();
      },
      get: () => editor?.isActive('bold') || false,
    },
    fontWeight: {
      set: (value) => {
        if (value === 'regular') {
          editor?.chain().focus().unsetBold().run();
        }

        if (value === 'bold') {
          editor?.chain().focus().setBold().run();
        }
      },
    },
    fontFamily: {
      set: (value) => {
        editor?.chain().focus().setFontFamily(value).run();
      },
      unset: () => {
        editor?.chain().focus().unsetFontFamily().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetFontFamily().blur().run();
      },
      get: (mixedValue = '-') => {
        const fontFamily = editor?.getAttributes('textStyle').fontFamily;
        // No value mean parent node value
        if (!fontFamily) {
          return '';
        }
        if (editor?.isActive('textStyle', { fontFamily })) {
          return fontFamily;
        }
        return mixedValue;
      },
    },
    color: {
      set: (value) => {
        editor?.chain().focus().setColor(value).run();
      },
      unset: () => {
        editor?.chain().focus().unsetColor().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetColor().run();
      },
      get: () => {
        const color = editor?.getAttributes('textStyle').color;
        if (editor?.isActive('textStyle', { color })) {
          return color;
        }
        return color;
      },
    },
    fontSize: {
      set: (value) => {
        editor?.chain().focus().setFontSize(value).run();
      },
      unset: () => {
        editor?.chain().focus().unsetFontSize().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetFontSize().run();
      },
      // we always want to show a value for the font size picker,
      // otherwise the UX is confusing
      // so we can not just return a mixed value marker
      get: () => {
        return hasSelection(editor)
          ? markUnderCursorForType(editor, 'textStyle')?.attrs.fontSize
          : editor?.getAttributes('textStyle').fontSize;
      },
    },
    italic: {
      set: () => editor?.chain().focus().setItalic().run(),
      unset: () => {
        editor?.chain().focus().unsetItalic().run();
      },
      toggle: () => {
        editor?.chain().focus().toggleItalic().run();
      },
      get: () => editor?.isActive('italic') || false,
      setAll: () => {
        editor?.chain().selectAll().setItalic().blur().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetItalic().blur().run();
      },
    },
    bulletList: {
      toggle: () => {
        editor?.chain().focus().toggleBulletList().run();
      },
      get: () => editor?.isActive('customBulletList') || false,
    },
    orderedList: {
      toggle: () => {
        editor?.chain().focus().toggleOrderedList().run();
      },
      get: () => editor?.isActive('customOrderedList') || false,
    },
    shadow: {
      set: (value) => {
        editor?.chain().focus().setShadow(value).run();
      },
      unset: () => {
        editor?.chain().focus().unsetShadow().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetShadow().run();
      },
      get: () => {
        const shadow = editor?.getAttributes('textStyle').shadow;
        if (editor?.isActive('textStyle', { shadow })) {
          return shadow;
        }
        return shadow;
      },
    },
    strikethrough: {
      toggle: () => {
        editor?.chain().focus().toggleStrike().run();
      },
      get: () => editor?.isActive('strike') || false,
    },
    underline: {
      toggle: () => {
        editor?.chain().focus().toggleUnderline().run();
      },
      get: () => editor?.isActive('underline') || false,
    },
    subscript: {
      toggle: () => {
        // TODO: Chain these commands once https://github.com/ueberdosis/tiptap/issues/5792 gets resolved
        editor?.chain().focus().toggleSubscript().run();
        editor?.commands.unsetSuperscript();
      },
      get: () => editor?.isActive('subscript') || false,
    },
    superscript: {
      toggle: () => {
        // TODO: Chain these commands once https://github.com/ueberdosis/tiptap/issues/5792 gets resolved
        editor?.chain().focus().toggleSuperscript().run();
        editor?.commands.unsetSubscript();
      },
      get: () => editor?.isActive('superscript') || false,
    },
    link: {
      set: (value) => {
        editor?.chain().focus().setLink(value).run();
      },
      unset: () => {
        editor?.chain().focus().unsetLink().run();
      },
      unsetAll: () => {
        editor?.chain().selectAll().unsetLink().run();
      },
      get: () => {
        return editor?.getAttributes('link').href;
      },
    },
    selection: {
      replace: (content) => {
        replaceSelectedText(content, editor);
      },
    },
  };
}
